import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  useTheme,
  Menu,
  MenuItem,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import {
  calculateFittedVisibleTabCount,
  getHiddenTabs,
  getPinnedVisibleTabs,
  getTabMeasurementKey
} from './tabOverflow';

export interface TabConfig {
  key: string | number;
  label: string;
  count?: number;
  icon?: React.ReactNode;
}

export interface UnifiedTabsProps {
  tabs: TabConfig[];
  activeTab: string | number;
  onTabChange: (tabKey: string | number) => void;
  variant?: 'primary' | 'secondary';
  maxVisibleTabs?: number;
  fitToContainer?: boolean;
  minVisibleTabs?: number;
}

const areNumberRecordsEqual = (
  left: Record<string, number>,
  right: Record<string, number>
) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left[key] === right[key]);
};

const estimateTabWidth = (tab: TabConfig, isMobile: boolean) => {
  const horizontalPadding = isMobile ? 24 : 32;
  const iconWidth = tab.icon ? 20 : 0;
  const labelWidth = tab.label.length * 7.5;
  const countWidth =
    tab.count !== undefined && !isMobile
      ? Math.max(20, String(tab.count).length * 7 + 12) + 6
      : 0;

  return horizontalPadding + iconWidth + labelWidth + countWidth;
};

const estimateMoreButtonWidth = (hiddenCount: number) =>
  58 + String(hiddenCount).length * 8;

export const UnifiedTabs: React.FC<UnifiedTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  maxVisibleTabs,
  fitToContainer = false,
  minVisibleTabs = 1
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabMeasureRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const moreMeasureRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [tabWidths, setTabWidths] = useState<Record<string, number>>({});
  const [moreButtonWidths, setMoreButtonWidths] = useState<
    Record<string, number>
  >({});

  // Auto-calculate visible tabs based on screen size if not specified
  const effectiveMaxTabs = maxVisibleTabs !== undefined 
    ? maxVisibleTabs 
    : isMobile 
      ? (tabs.length > 3 ? 2 : 3) // Show 2 tabs + "more" if 4+, otherwise show all up to 3
      : tabs.length;

  const requestedVisibleCount = Math.min(effectiveMaxTabs, tabs.length);
  const horizontalPadding = isMobile ? 32 : 64;
  const itemGap = isMobile ? 4 : 8;

  useLayoutEffect(() => {
    if (!fitToContainer || !containerRef.current) {
      return;
    }

    const updateMeasurements = () => {
      const nextContainerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 0;
      const nextTabWidths: Record<string, number> = {};
      const nextMoreButtonWidths: Record<string, number> = {};

      tabMeasureRefs.current.forEach((element, key) => {
        nextTabWidths[key] = element.getBoundingClientRect().width;
      });
      moreMeasureRefs.current.forEach((element, hiddenCount) => {
        nextMoreButtonWidths[String(hiddenCount)] =
          element.getBoundingClientRect().width;
      });

      setContainerWidth((current) =>
        current === nextContainerWidth ? current : nextContainerWidth
      );
      setTabWidths((current) =>
        areNumberRecordsEqual(current, nextTabWidths)
          ? current
          : nextTabWidths
      );
      setMoreButtonWidths((current) =>
        areNumberRecordsEqual(current, nextMoreButtonWidths)
          ? current
          : nextMoreButtonWidths
      );
    };

    updateMeasurements();

    const resizeObserver = new ResizeObserver(updateMeasurements);
    resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', updateMeasurements);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateMeasurements);
    };
  }, [activeTab, fitToContainer, isMobile, tabs]);

  const fittedVisibleCount = useMemo(() => {
    if (!fitToContainer) {
      return requestedVisibleCount;
    }

    return calculateFittedVisibleTabCount({
      tabs,
      activeTab,
      maxVisibleTabs: requestedVisibleCount,
      minVisibleTabs,
      containerWidth,
      horizontalPadding,
      itemGap,
      tabWidths,
      moreButtonWidths,
      estimateTabWidth: (tab) => estimateTabWidth(tab, isMobile),
      estimateMoreButtonWidth
    });
  }, [
    activeTab,
    containerWidth,
    fitToContainer,
    horizontalPadding,
    isMobile,
    itemGap,
    minVisibleTabs,
    moreButtonWidths,
    requestedVisibleCount,
    tabWidths,
    tabs
  ]);

  const visibleTabs = getPinnedVisibleTabs(
    tabs,
    activeTab,
    fittedVisibleCount
  );
  const hiddenTabs = getHiddenTabs(tabs, visibleTabs);

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleTabSelect = (tabKey: string | number) => {
    onTabChange(tabKey);
    handleMoreMenuClose();
  };

  const tabButtonSx = (isActive: boolean) => ({
    px: isMobile ? 1.5 : 2,
    py: 0.75,
    borderRadius: '6px',
    border: '1px solid',
    borderColor: isActive
      ? theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : theme.palette.divider
      : 'transparent',
    backgroundColor: isActive
      ? theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)'
      : 'transparent',
    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
    fontSize: '13px',
    fontWeight: isActive ? 600 : 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    position: 'relative',
    mb: '-1px', // Pull tab down by 1px to sit on divider
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': {
      color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
      borderColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : theme.palette.divider,
      backgroundColor: isActive
        ? theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.02)'
        : theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)'
    },
    // Underline accent on the divider line
    ...(isActive
      ? {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-9px', // Position 8px below the tab box
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '2px 2px 0 0'
          }
        }
      : {})
  });

  const moreButtonSx = {
    px: 1.5,
    py: 0.75,
    borderRadius: '6px',
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': {
      color: theme.palette.text.primary,
      borderColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : theme.palette.divider,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.01)'
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        gap: isMobile ? 0.5 : 1,
        px: isMobile ? 2 : 4,
        pt: 0,
        pb: 0,
        height: '48px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        position: 'relative',
        minWidth: 0,
        width: '100%',
        background: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}
    >
      {fitToContainer && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: 0,
            overflow: 'hidden',
            visibility: 'hidden',
            pointerEvents: 'none',
            display: 'flex',
            gap: isMobile ? 0.5 : 1
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;

            return (
              <Box
                key={tab.key}
                ref={(element: HTMLDivElement | null) => {
                  const measurementKey = getTabMeasurementKey(tab.key);

                  if (element) {
                    tabMeasureRefs.current.set(measurementKey, element);
                  } else {
                    tabMeasureRefs.current.delete(measurementKey);
                  }
                }}
                sx={tabButtonSx(isActive)}
              >
                {tab.icon && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      opacity: 0.7
                    }}
                  >
                    {tab.icon}
                  </Box>
                )}
                {tab.label}
                {tab.count !== undefined && !isMobile && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px',
                      height: '20px',
                      px: 0.75,
                      ml: 0.75,
                      borderRadius: '6px',
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.08)',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      lineHeight: 1
                    }}
                  >
                    {tab.count}
                  </Box>
                )}
              </Box>
            );
          })}
          {Array.from({ length: tabs.length }, (_, index) => index + 1).map(
            (hiddenCount) => (
              <Box
                key={hiddenCount}
                ref={(element: HTMLDivElement | null) => {
                  if (element) {
                    moreMeasureRefs.current.set(hiddenCount, element);
                  } else {
                    moreMeasureRefs.current.delete(hiddenCount);
                  }
                }}
                sx={moreButtonSx}
              >
                +{hiddenCount} more
              </Box>
            )
          )}
        </Box>
      )}

      {visibleTabs.map((tab) => {
        const isActive = activeTab === tab.key;

        return (
          <Box
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            sx={tabButtonSx(isActive)}
          >
            {tab.icon && (
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  opacity: 0.7
                }}
              >
                {tab.icon}
              </Box>
            )}
            {tab.label}
            {tab.count !== undefined && !isMobile && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '20px',
                  height: '20px',
                  px: 0.75,
                  ml: 0.75,
                  borderRadius: '6px',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.08)',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  lineHeight: 1
                }}
              >
                {tab.count}
              </Box>
            )}
          </Box>
        );
      })}

      {/* +X More Button */}
      {hiddenTabs.length > 0 && (
        <>
          <Box
            onClick={handleMoreMenuOpen}
            sx={moreButtonSx}
          >
            +{hiddenTabs.length} more
          </Box>

          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={handleMoreMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 160,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0,0,0,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`
              }
            }}
            MenuListProps={{
              sx: {
                py: 0.5
              }
            }}
          >
            {hiddenTabs.map((tab) => (
              <MenuItem
                key={tab.key}
                onClick={() => handleTabSelect(tab.key)}
                selected={activeTab === tab.key}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: '13px',
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  color: activeTab === tab.key 
                    ? theme.palette.primary.main 
                    : theme.palette.text.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '4px',
                  mx: 0.5,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.04)'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                {tab.icon && (
                  <Box 
                    component="span" 
                    sx={{ 
                      display: 'inline-flex', 
                      alignItems: 'center',
                      mr: 1.5,
                      opacity: 0.7
                    }}
                  >
                    {tab.icon}
                  </Box>
                )}
                <ListItemText 
                  primary={tab.label}
                  primaryTypographyProps={{ 
                    fontSize: '13px',
                    fontWeight: activeTab === tab.key ? 600 : 400
                  }}
                />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

export const UnifiedTabsContainer: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}
    >
      {children}
    </Box>
  );
};
